/**
 * AudienceContext + TrackProvider — hardened production split.
 *
 * As of v0.42 we treat Community and Student as two FULLY
 * SEPARATE tracks of the app. The same React build serves both, but
 * once a visitor lands they pick one, the choice is locked into a
 * cookie, and they never see the other track's pages, menu items, or
 * audience toggle UI until they explicitly clear or switch tracks.
 *
 * Founder backdoor:
 *   navigating to `/__founder/<ADMIN_KEY>` sets a session-only
 *   `founder_mode` flag that re-enables the legacy audience toggle UI
 *   so the founder can audit both tracks live without clearing cookies.
 *   The backdoor is session-scoped (sessionStorage), so closing the
 *   tab forgets it.
 *
 * Migration:
 *   Legacy `maldek_audience_v1` keys are wiped on first read of v0.42+
 *   so existing visitors see the splash again post-launch (the wipe is
 *   idempotent — the "v2 migrated" sentinel prevents re-wiping).
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const KEY = "maldek_audience_v2";           // current cookie
const LEGACY_KEY = "maldek_audience_v1";    // pre-track-split storage
const MIGRATION_SENTINEL = "maldek_audience_migrated_v2";
const FOUNDER_KEY = "maldek_founder_mode";  // sessionStorage only

/**
 * v0.50 (Jul 2026 pivot): the platform serves exactly TWO learner views —
 * Nursing Student and Nurse Practitioner Student. Both live on the
 * underlying "student" track (route gating below is unchanged); the
 * nursing/NP distinction is a VIEW handled by `lib/view.js`.
 */
export const AUDIENCES = [
  {
    id: "nursing",
    label: "Nursing Student",
    short: "I want to think clinically and be NCLEX-ready",
    tagline: "Prioritization, mechanism, bedside safety. Undergrad-nursing through new-grad depth.",
    accent: "#6B8F71",
    icon: "·",
  },
  {
    id: "np",
    label: "Nurse Practitioner Student",
    short: "I want to diagnose and manage with confidence",
    tagline: "Differential, workup, management. The same clinical library at advanced-practice depth.",
    accent: "#6B8F71",
    icon: "··",
  },
];

/**
 * Route visibility map — every page on the site shows up here. Used
 * for both menu visibility AND hard route-access gating. If a page
 * isn't in the map, the default is `community + student + clinician`
 * (visible to all).
 */
export const AUDIENCE_ROUTES = {
  // Home + Landing — both tracks land here (content branches internally).
  "/":                     { community: true, student: true, clinician: true },

  // Track-specific home pages — strictly walled off.
  "/community":            { community: true, student: false, clinician: false },
  "/student":              { community: false, student: true, clinician: true },
  "/ce-hub":               { community: false, student: true, clinician: true },
  "/cna-academy":          { community: true, student: true, clinician: true },
  "/cna-admin":            { community: true, student: true, clinician: true },
  "/bls-academy":          { community: true, student: true, clinician: true },

  // Plain-English library — appropriate for all three.
  "/explain":              { community: true, student: true, clinician: true },
  "/lab-reference":        { community: true, student: true, clinician: true },

  // Walkthrough + Reasoning Engine (Forward / Reverse) — open to all.
  "/walkthrough":          { community: true, student: true, clinician: true },
  "/reasoning-engine":     { community: true, student: true, clinician: true },

  // Cultural cooking & health — community-only.
  "/cooking":              { community: true, student: false, clinician: false },
  // Household Health Story Simulation + SDOH Intelligence Engine.
  // Community-facing spec page; primarily for patients & caregivers
  // exploring how household + social factors shape outcomes.
  "/household-sim":        { community: true, student: true, clinician: true },

  // NCLEX Quiz Builder — student/clinician practice tool.
  "/nclex":                { community: false, student: true, clinician: true },
  "/nclex/dashboard":      { community: false, student: true, clinician: true },
  // Master/creator console — visible to anyone with the master code,
  // gated by its own login form. We mark it true across tracks so the
  // TrackGate doesn't wrong-track-bounce someone who shares the URL.
  "/__master":             { community: true, student: true, clinician: true },

  // Practice the Visit — disabled site-wide (legacy route reachable
  // by direct URL but not menu-exposed).
  "/practice-visit":       { community: false, student: false, clinician: false },

  // Imaging & Procedures — community-facing demystifier for the
  // most common modern (2020+) routine procedures and scans, indexed
  // by age group.
  "/imaging-procedures":   { community: true, student: true, clinician: true },

  // Reasoning / Dashboard / Curriculum — clinician track only.
  "/reasoning":            { community: false, student: true, clinician: true },

  // SDoH safety-net catalog — Reference for every audience.
  "/safety-net":           { community: true, student: true, clinician: true },

  // Practice surfaces — Cognitive Simulator + Reasoning Engine are
  // clinician/student-only by founder direction (community has its
  // own self-coaching tools). Patient stays practitioner-only.
  "/cogsim":               { community: false, student: true, clinician: true },
  "/time-machine":         { community: false, student: true, clinician: true },
  "/patient":              { community: false, student: true, clinician: true },
  "/reasoning-engine":     { community: false, student: true, clinician: true },
  "/therapeutics":         { community: true, student: true, clinician: true },

  // Household Simulator — community-only per founder direction
  // (SDOH risk story is patient-facing). Hidden from student/clinician
  // nav but the route remains reachable via direct URL.
  "/household-sim":        { community: true, student: false, clinician: false },

  // Live AI synthesis surface — accessible to every audience; the
  // page itself adapts voice based on the audience query param.
  "/synth":                { community: true, student: true, clinician: true },

  // Cohort dashboard etc — clinician/founder.
  "/cohort":               { community: false, student: true, clinician: true },

  // For-institutions is a public marketing surface — visible regardless
  // of which track the visitor picked. Institution picks on the splash
  // route here directly.
  "/for-institutions":     { community: true, student: true, clinician: true, institution: true },
};


/**
 * Returns true iff the audience can navigate to this path.
 * Strips `?query` and `#hash` so dynamic sub-routes resolve the same
 * way as their bare path. Unmapped routes are open to everyone (the
 * router below redirects unknown URLs anyway).
 */
export function isRouteVisibleFor(path, audienceId) {
  const base = String(path || "").split("?")[0].split("#")[0];
  const cfg = AUDIENCE_ROUTES[base];
  if (!cfg) return true;
  return !!cfg[audienceId];
}


const AudienceCtx = createContext({
  audience: null,
  setAudience: () => {},
  founderMode: false,
  isCommunity: false,
  isStudent: false,
  isClinician: false,
  resetTrack: () => {},
});

function readFounderMode() {
  try { return sessionStorage.getItem(FOUNDER_KEY) === "1"; }
  catch { return false; }
}

export function setFounderMode(on) {
  try {
    if (on) sessionStorage.setItem(FOUNDER_KEY, "1");
    else sessionStorage.removeItem(FOUNDER_KEY);
  } catch { /* ignore */ }
}

export function AudienceProvider({ children }) {
  // One-time migration: clear the legacy v1 storage so existing
  // visitors see the splash again.
  useEffect(() => {
    try {
      if (!localStorage.getItem(MIGRATION_SENTINEL)) {
        localStorage.removeItem(LEGACY_KEY);
        localStorage.setItem(MIGRATION_SENTINEL, "1");
      }
    } catch { /* ignore */ }
  }, []);

  // Jul 2026 pivot: the platform is student-only (Nursing + NP views).
  // Every visitor rides the "student" track — no splash, no community gate.
  const [audience, setAudienceState] = useState("student");
  const [founderMode, setFounderModeState] = useState(readFounderMode);

  // Re-read founder mode on every URL change (the splash + gate
  // components flip the flag via setFounderMode and rely on us
  // picking it up).
  useEffect(() => {
    const handler = () => setFounderModeState(readFounderMode());
    window.addEventListener("storage", handler);
    window.addEventListener("maldek-founder-toggle", handler);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener("maldek-founder-toggle", handler);
    };
  }, []);

  const setAudience = useCallback((id) => {
    // All roles (nursing / np / legacy ids) resolve to the student track.
    setAudienceState("student");
    try {
      localStorage.setItem(KEY, "student");
      localStorage.setItem("maldek_role", id);
    } catch { /* ignore */ }
  }, []);

  const resetTrack = useCallback(() => {
    setAudienceState("student");
    try { localStorage.setItem(KEY, "student"); } catch { /* ignore */ }
  }, []);

  return (
    <AudienceCtx.Provider value={{
      audience,
      setAudience,
      resetTrack,
      founderMode,
      isCommunity: audience === "community",
      isStudent:   audience === "student",
      isClinician: audience === "student",
    }}>
      {children}
    </AudienceCtx.Provider>
  );
}

export const useAudience = () => useContext(AudienceCtx);

export const audienceMeta = (id) =>
  AUDIENCES.find((a) => a.id === id) || AUDIENCES[0];
